import { Field, Formik } from "formik"
import { useContext, useEffect, useState } from "react"
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input"
import "react-phone-number-input/style.css"
import { Link, useLocation, useNavigate } from "react-router-dom"
import * as Yup from "yup"
import { authApi, getErrorMessage, locationsApi } from "../../../api"
import FormCard from "../../../components/FormCard"
import RoundedEdgeButton from "../../../components/RoundedEdgeButton"
import { APIContext } from "../../../connector"
import { useToast } from "../../../hooks/useToast"
import logo from "../../../static/images/Logo.png"
import classes from "./Register.module.scss"

const schema = Yup.object().shape({
  name: Yup.string().required("Name is a required field"),
  email: Yup.string()
    .required("Email is a required field")
    .email("Invalid email format"),
  user_type: Yup.string().required("User type is a required field"),
  gender: Yup.string().required("Gender is required"),
  zone: Yup.string().required("Zone is required"),
  agree: Yup.boolean().oneOf([true], "You must accept the terms and conditions"),
  phone_number: Yup.string()
    .required("Phone number is required")
    .test("is-valid-phone", "Invalid phone number", (value) => {
      return isValidPhoneNumber(value || "")
    }),
})

const Register = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { setUserData, setIsAuthenticated } = useContext(APIContext);
  const { openToast } = useToast();
  const [zones, setZones] = useState([]);

  useEffect(() => {
    const fetchZones = async () => {
      try {
        const data = await locationsApi.getZones();
        setZones(data);
      } catch (err) {
        console.error("Error fetching zones:", err);
      }
    };
    fetchZones();
  }, []);

  const handleSubmit = async (values) => {
    try {
      const googleAuthToken =
        state?.provider === "microsoft"
          ? `microsoft:${state?.microsoft_sub}`
          : state?.access_token;

      // Server will set session cookies on successful registration
      const data = await authApi.register({ ...values, ...state, google_auth_token: googleAuthToken});

      setUserData(data);
      setIsAuthenticated(true);

      if (data.user_type === "CLIENT") {
        navigate("/booking", { state: { ...data } });
      } else {
        navigate("/driverRegister", { state: { ...data } });
      }
    } catch (error) {
      let msg = getErrorMessage(error);
      // Simple cleanup to capitalize first letter
      msg = msg.charAt(0).toUpperCase() + msg.slice(1);
      openToast(msg, 5000, "danger");
    }
  };

  return (
    <>
      <div className={classes.container}>
        <img src={logo || "/placeholder.svg"} alt="Logo" className={classes.logo} />
        {/* Wrapping form inside formik tag and passing our schema to validationSchema prop */}
        <Formik
          validationSchema={schema}
          initialValues={{
            name: state?.name,
            email: state?.email,
            phone_number: "",
            user_type: "",
            gender: "",
            zone: "",
            agree: false,
          }}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
            <>
              <form noValidate onSubmit={handleSubmit}>
                <FormCard className={classes.registerCard}>
                  <div>
                    {/* Passing handleSubmit parameter tohtml form onSubmit property */}
                    <p className={classes.title}>Create New Account</p>
                    <p className={classes.subtitle}>
                      Already have an account?{" "}
                      <Link to="/login" className={classes.link}>
                        Log in here
                      </Link>
                    </p>
                    {/* Our input html with passing formik parameters like handleChange, values, handleBlur to input properties */}
                    <div className={classes.inputContainer}>
                      <p className={classes.inputTitle}>Name</p>
                      <input
                        // type="name"
                        name="name"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.name}
                        placeholder="Name"
                        className={classes.input}
                        id="name"
                      />
                      <p className={classes.error}>{errors.name && touched.name && errors.name}</p>
                    </div>
                    <div className={classes.inputContainer}>
                      <p className={classes.inputTitle}>Gender</p>
                      <ul className={classes.chooseUser}>
                        <li>
                          <Field type="radio" id="male" name="gender" value="M" />
                          <label htmlFor="male">Male</label>
                        </li>
                        <li>
                          <Field type="radio" id="female" name="gender" value="F" />
                          <label htmlFor="female">Female</label>
                        </li>
                      </ul>
                      <p className={classes.error}>{errors.gender && touched.gender && errors.gender}</p>
                    </div>
                    {/* <p className="error"{errors.gender && touched.gender && errors.gender}></p> */}
                    <div className={classes.inputContainer}>
                      <p className={classes.inputTitle}>Email</p>
                      <input
                        type="email"
                        name="email"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.email}
                        placeholder="Email"
                        className={classes.input}
                        id="email"
                        disabled
                      />
                      <p className={classes.error}>{errors.email && touched.email && errors.email}</p>
                    </div>
                    <div className={classes.inputContainer}>
                      <p className={classes.inputTitle}>Phone Number</p>
                      <PhoneInput
                        className={classes.phoneInput}
                        placeholder="Enter phone number"
                        international
                        countryCallingCodeEditable={false}
                        defaultCountry="MY"
                        name="phone_number"
                        value={values.phone_number}
                        onChange={(value) => {
                          handleChange({ target: { name: "phone_number", value } })
                        }}
                        onBlur={handleBlur}
                        id="phone_number"
                      />
                      <p className={classes.error}>{errors.phone_number && touched.phone_number && errors.phone_number}</p>
                    </div>
                    <div className={classes.inputContainer}>
                      <ul className={classes.chooseUser}>
                        <li>
                          <Field type="radio" id="user" name="user_type" value="CLIENT" />
                          <label htmlFor="user">User</label>
                        </li>
                        <li>
                          <Field type="radio" id="rider" name="user_type" value="DRIVER" />
                          <label htmlFor="rider">Driver</label>
                        </li>
                      </ul>
                      <p className={classes.error}>{errors.user_type && errors.user_type}</p>
                    </div>

                    <div className={classes.inputContainer}>
                      <p className={classes.inputTitle}>Zone (Campus)</p>
                      <select
                        name="zone"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.zone}
                        className={classes.input}
                        id="zone"
                      >
                        <option value="" disabled>Select your campus</option>
                        {zones.map((zone) => (
                          <option key={zone.zone_id} value={zone.zone_id}>
                            {zone.name}
                          </option>
                        ))}
                      </select>
                      <p className={classes.error}>{errors.zone && touched.zone && errors.zone}</p>
                    </div>

                    <div>
                      <div className="consent">
                        <input
                          type="checkbox"
                          name="agree"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          checked={values.agree}
                          className="form-check-input"
                          id="agree"
                        />
                        <label htmlFor="agree" className="form-check-label">
                          I have read and agree to the{" "}
                          <a
                            href="https://docs.google.com/document/d/1EOjf52te3VXuTN7GmFy7DZOb8KwsyO-pen6rwxM3D5Q/edit?usp=drivesdk"
                            target="_blank"
                            rel="noreferrer"
                          >
                            terms and conditions
                          </a>
                        </label>
                      </div>
                      <p className={classes.error}>{errors.agree && errors.agree}</p>
                    </div>
                    {/* Click on submit button to submit the form */}
                  </div>
                </FormCard>
                <RoundedEdgeButton type="submit" className={classes.signupButton}>
                  Sign Up
                </RoundedEdgeButton>
              </form>
            </>
          )}
        </Formik>
      </div>
    </>
  )
}

export default Register
